import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { SparklesIcon, StarIcon } from '@heroicons/react/24/solid';
import MobileLayout from '../components/MobileLayout';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

const getStripePromise = () => {
  const isTestUser = localStorage.getItem('isTestUser') === 'true';
  const key = isTestUser
    ? process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_TEST
    : process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_LIVE;
  return loadStripe(key || '');
};

interface Tier {
  id: string;
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  features: string[];
  highlight?: boolean;
  badge?: string;
}

const TIER_ORDER = ['free', 'plus', 'pro', 'ultra', 'max'];

const Subscription: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [currentPlan, setCurrentPlan] = useState<string>('free');
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>('none');
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [showCancelModal, setShowCancelModal] = useState(false);

  const tiers: Tier[] = [
    {
      id: 'free',
      name: t('pricing.free', 'Free'),
      monthlyPrice: 0,
      yearlyPrice: 0,
      features: [
        t('pricing.features.fiftyMsgsDay', '50 messages per day'),
        t('pricing.features.oneCompanion', '1 companion'),
        t('pricing.features.basicFeatures', 'Basic features'),
      ],
    },
    {
      id: 'plus',
      name: t('pricing.plus', 'Plus'),
      monthlyPrice: 7.99,
      yearlyPrice: 59.99,
      features: [
        t('pricing.features.fifteenKTokens', '15,000 monthly tokens'),
        t('pricing.features.fiveCompanions', '5 companions'),
        t('pricing.features.codexUploads', 'Codex uploads'),
        t('pricing.features.memory', 'Enhanced memory'),
      ],
    },
    {
      id: 'pro',
      name: t('pricing.pro', 'Pro'),
      monthlyPrice: 14.99,
      yearlyPrice: 119.99,
      highlight: true,
      badge: t('pricing.popular', 'Popular'),
      features: [
        t('pricing.features.fortyKTokens', '40,000 monthly tokens'),
        t('pricing.features.fifteenCompanions', '15 companions'),
        t('pricing.features.imageUpload', 'Image upload'),
        t('pricing.features.tarotReadings', 'Tarot readings'),
        t('pricing.features.dataExports', 'Data exports'),
      ],
    },
    {
      id: 'ultra',
      name: t('pricing.ultra', 'Ultra'),
      monthlyPrice: 49.99,
      yearlyPrice: 399.99,
      features: [
        t('pricing.features.oneHundredFiftyKTokens', '150,000 monthly tokens'),
        t('pricing.features.unlimitedCompanions', 'Unlimited companions'),
        t('pricing.features.voiceMessages', 'Voice messages'),
        t('pricing.features.prioritySupport', 'Priority support'),
      ],
    },
    {
      id: 'max',
      name: t('pricing.max', 'Max'),
      monthlyPrice: 99.99,
      yearlyPrice: 799.99,
      badge: t('pricing.bestValue', 'Best Value'),
      features: [
        t('pricing.features.unlimitedTokens', 'Unlimited tokens'),
        t('pricing.features.unlimitedCompanions', 'Unlimited companions'),
        t('pricing.features.allFeatures', 'All features included'),
        t('pricing.features.priorityEverything', 'Priority everything'),
      ],
    },
  ];

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get('/subscriptions/status');
      setCurrentPlan(response.data.plan || 'free');
      setSubscriptionStatus(response.data.status || 'none');
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    }
  };

  const getSavingsPercent = (tier: Tier) => {
    if (tier.monthlyPrice === 0) return 0;
    const yearlyMonthly = tier.yearlyPrice / 12;
    return Math.round(((tier.monthlyPrice - yearlyMonthly) / tier.monthlyPrice) * 100);
  };

  const getTierIndex = (tierId: string) => TIER_ORDER.indexOf(tierId);
  const isUpgrade = (tierId: string) => getTierIndex(tierId) > getTierIndex(currentPlan);
  const isDowngrade = (tierId: string) => getTierIndex(tierId) < getTierIndex(currentPlan);
  const hasActiveSubscription =
    currentPlan !== 'free' && ['active', 'trialing'].includes(subscriptionStatus);

  const handleSubscribe = async (planId: string) => {
    if (planId === 'free') return;
    setIsLoading(planId);

    try {
      // Map yearly to annual for the API
      const apiBillingPeriod = billingPeriod === 'yearly' ? 'annual' : 'monthly';

      if (hasActiveSubscription) {
        // Upgrade/downgrade existing subscription
        const response = await axios.put('/subscriptions/update', {
          planId,
          billingPeriod: apiBillingPeriod,
        });
        if (response.data.success) {
          await fetchSubscriptionStatus();
        }
      } else {
        // New subscription — create checkout session
        const response = await axios.post('/subscriptions/checkout-session', {
          planId,
          billingPeriod: apiBillingPeriod,
          successUrl: `${window.location.origin}/subscription/success`,
          cancelUrl: `${window.location.origin}/subscription`,
        });

        const { sessionId, sessionUrl } = response.data;

        if (sessionUrl) {
          window.location.href = sessionUrl;
        } else if (sessionId) {
          const stripe = await getStripePromise();
          if (stripe) {
            const { error } = await stripe.redirectToCheckout({ sessionId });
            if (error) {
              console.error('Stripe redirect error:', error);
              toast.error(
                t(
                  'subscription.checkoutError',
                  'Failed to redirect to checkout. Please try again.',
                ),
              );
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to process subscription:', error);
      toast.error(
        t('subscription.subscriptionError', 'Failed to start subscription. Please try again.'),
      );
    } finally {
      setIsLoading(null);
    }
  };

  const handleManageBilling = async () => {
    setIsLoading('manage');
    try {
      const response = await axios.post('/subscriptions/portal', {
        returnUrl: window.location.href,
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Failed to open customer portal:', error);
      toast.error(
        t('subscription.portalError', 'Failed to open subscription management. Please try again.'),
      );
    } finally {
      setIsLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    setIsLoading('cancel');
    try {
      const response = await axios.delete('/subscriptions/cancel');
      if (response.data.success) {
        setShowCancelModal(false);
        await fetchSubscriptionStatus();
      }
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      toast.error(
        t('subscription.cancelError', 'Failed to cancel subscription. Please try again.'),
      );
    } finally {
      setIsLoading(null);
    }
  };

  const getPrice = (tier: Tier) => {
    return billingPeriod === 'yearly' ? tier.yearlyPrice : tier.monthlyPrice;
  };

  const getButtonLabel = (tier: Tier) => {
    if (tier.id === 'free') return null;
    if (isUpgrade(tier.id)) return t('subscription.upgrade', 'Upgrade');
    if (isDowngrade(tier.id)) return t('subscription.downgrade', 'Downgrade');
    return t('pricing.subscribe', 'Subscribe');
  };

  return (
    <MobileLayout>
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-light-border dark:border-dark-border">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-light-surface dark:hover:bg-dark-surface rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-light-text dark:text-dark-text" />
          </button>
          <h1 className="text-lg font-semibold text-light-text dark:text-dark-text">
            {t('pricing.title', 'Choose Your Plan')}
          </h1>
          <div className="w-9" />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Icon and Title */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-500/20 rounded-3xl mb-3">
              <SparklesIcon className="w-8 h-8 text-primary-500" />
            </div>
            <h2 className="text-xl font-bold text-light-text dark:text-dark-text mb-1">
              {t('pricing.headline', 'Unlock Your Full Experience')}
            </h2>
            <p className="text-light-text-secondary dark:text-dark-text-secondary text-sm">
              {t('pricing.subtitle', 'Find the perfect plan for your journey')}
            </p>
          </div>

          {/* Manage Billing Button (if subscribed) */}
          {hasActiveSubscription && (
            <div className="flex gap-2 mb-6">
              <button
                onClick={handleManageBilling}
                disabled={isLoading === 'manage'}
                className="flex-1 py-3 bg-light-surface dark:bg-dark-surface hover:bg-gray-100 dark:hover:bg-dark-border border border-light-border dark:border-dark-border text-light-text dark:text-dark-text font-semibold rounded-xl transition-colors text-sm"
              >
                {isLoading === 'manage'
                  ? t('common.loading', 'Loading...')
                  : t('subscription.manageBilling', 'Manage Billing')}
              </button>
              <button
                onClick={() => setShowCancelModal(true)}
                className="px-4 py-3 bg-red-500/10 hover:bg-red-500/20 text-red-500 font-semibold rounded-xl transition-colors text-sm"
              >
                {t('subscription.cancel', 'Cancel')}
              </button>
            </div>
          )}

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <span
              className={`text-sm ${billingPeriod === 'monthly' ? 'text-light-text dark:text-dark-text font-semibold' : 'text-light-text-secondary dark:text-dark-text-secondary'}`}
            >
              {t('pricing.monthly', 'Monthly')}
            </span>
            <button
              onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
              className={`relative w-12 h-6 rounded-full transition-colors border border-white/30 ${billingPeriod === 'yearly' ? 'bg-primary-500' : 'bg-white/20'}`}
            >
              <div
                className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${billingPeriod === 'yearly' ? 'translate-x-6' : 'translate-x-0.5'}`}
              />
            </button>
            <span
              className={`text-sm ${billingPeriod === 'yearly' ? 'text-light-text dark:text-dark-text font-semibold' : 'text-light-text-secondary dark:text-dark-text-secondary'}`}
            >
              {t('pricing.yearly', 'Yearly')}
            </span>
            {billingPeriod === 'yearly' && (
              <span className="bg-green-500/20 text-green-500 text-xs px-2 py-0.5 rounded-full">
                {t('pricing.saveUpTo', 'Save up to 33%')}
              </span>
            )}
          </div>

          {/* Plan Cards */}
          <div className="space-y-4 mb-6">
            {tiers.map((tier) => {
              const isCurrent = currentPlan === tier.id;
              const savings = getSavingsPercent(tier);

              return (
                <div
                  key={tier.id}
                  className={`rounded-2xl p-5 border-2 transition-all backdrop-blur-md ${
                    tier.highlight
                      ? 'border-primary-500 bg-primary-500/20'
                      : isCurrent
                        ? 'border-green-500 bg-green-500/20'
                        : 'border-light-border dark:border-dark-border bg-light-surface/80 dark:bg-dark-surface/80'
                  }`}
                >
                  {/* Tier Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-light-text dark:text-dark-text">
                        {tier.name}
                      </h3>
                      {tier.badge && !isCurrent && (
                        <span className="bg-primary-500/20 text-primary-500 text-xs px-2 py-0.5 rounded-full">
                          {tier.badge}
                        </span>
                      )}
                      {isCurrent && (
                        <span className="bg-green-500/20 text-green-500 text-xs px-2 py-0.5 rounded-full">
                          {t('pricing.currentPlan', 'Current')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Price */}
                  <div className="flex items-baseline gap-1 mb-3">
                    <span className="text-3xl font-bold text-light-text dark:text-dark-text">
                      ${getPrice(tier).toFixed(2)}
                    </span>
                    {tier.monthlyPrice > 0 && (
                      <span className="text-light-text-secondary dark:text-dark-text-secondary text-sm">
                        /
                        {billingPeriod === 'yearly'
                          ? t('pricing.year', 'yr')
                          : t('pricing.month', 'mo')}
                      </span>
                    )}
                    {billingPeriod === 'yearly' && savings > 0 && (
                      <span className="text-green-500 text-xs ml-2">
                        {t('pricing.savePct', 'Save {{pct}}%', { pct: savings })}
                      </span>
                    )}
                  </div>

                  {/* Features */}
                  <div className="space-y-2 mb-4">
                    {tier.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckIcon className="w-4 h-4 text-primary-500 flex-shrink-0" />
                        <span className="text-sm text-light-text dark:text-dark-text">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Action Button */}
                  {isCurrent ? (
                    <div className="w-full py-3 text-center text-green-500 font-semibold text-sm">
                      {t('pricing.currentPlan', 'Current')}
                    </div>
                  ) : tier.id === 'free' ? (
                    hasActiveSubscription ? (
                      <button
                        onClick={() => setShowCancelModal(true)}
                        className="w-full py-3 bg-red-500/10 hover:bg-red-500/20 text-red-500 font-semibold rounded-xl transition-colors text-sm"
                      >
                        {t('subscription.downgradeToFree', 'Downgrade to Free')}
                      </button>
                    ) : (
                      <div className="w-full py-3 text-center text-light-text-secondary dark:text-dark-text-secondary text-sm">
                        {t('pricing.freePlan', 'Free forever')}
                      </div>
                    )
                  ) : (
                    <button
                      onClick={() => handleSubscribe(tier.id)}
                      disabled={isLoading === tier.id}
                      className={`w-full py-3 font-semibold rounded-xl transition-colors text-sm flex items-center justify-center gap-2 ${
                        isUpgrade(tier.id)
                          ? 'bg-primary-500 hover:bg-primary-600 text-white'
                          : isDowngrade(tier.id)
                            ? 'bg-orange-500/10 hover:bg-orange-500/20 text-orange-500'
                            : tier.highlight
                              ? 'bg-primary-500 hover:bg-primary-600 text-white'
                              : 'bg-primary-500/10 hover:bg-primary-500/20 text-primary-500'
                      }`}
                    >
                      <SparklesIcon className="w-4 h-4" />
                      {isLoading === tier.id
                        ? t('subscription.processing', 'Processing...')
                        : getButtonLabel(tier)}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Terms */}
          <p className="text-center text-light-text-secondary dark:text-dark-text-secondary text-xs">
            {t('subscription.cancelAnytime', 'Cancel anytime. Subscription renews monthly.')}
          </p>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-dark-surface rounded-2xl p-6 max-w-sm w-full">
            <div className="flex items-center justify-center w-12 h-12 bg-red-500/20 rounded-full mx-auto mb-4">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-center text-light-text dark:text-dark-text mb-2">
              {t('subscription.cancelConfirmTitle', 'Cancel Subscription?')}
            </h3>
            <p className="text-sm text-center text-light-text-secondary dark:text-dark-text-secondary mb-6">
              {t(
                'subscription.cancelConfirmMessage',
                'Your subscription will remain active until the end of your current billing period. After that, you will be downgraded to the Free plan.',
              )}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 py-3 bg-light-surface dark:bg-dark-border text-light-text dark:text-dark-text font-semibold rounded-xl transition-colors text-sm"
              >
                {t('common.keepIt', 'Keep It')}
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={isLoading === 'cancel'}
                className="flex-1 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl transition-colors text-sm"
              >
                {isLoading === 'cancel'
                  ? t('subscription.processing', 'Processing...')
                  : t('subscription.confirmCancel', 'Yes, Cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </MobileLayout>
  );
};

export default Subscription;
